import { Api, ApiError } from "./api";

/**
 * THE ONE OWNER OF "CONNECT A WALLET TO THIS SAVE".
 *
 * It lived inside `screens/profile.ts` as a closure over that screen's DOM. The victory screen now
 * needs the same flow - Alex, 2026-08-16: *"an unlinked player should get prompted to connect a
 * wallet to get their reward"* - and a prompt that sends the player to another screen to find a
 * button is a signpost, not a prompt.
 *
 * ⛔ **COPYING IT WOULD HAVE BEEN THE DEFECT.** The nonce, the exact `personal_sign` shape and the
 * server call are a three-step handshake; two copies drift, and the half that drifts fails at the
 * signature, which reads to a player as "my wallet is broken". One owner, two callers.
 *
 * ⚠️ **EIP-1193 DIRECTLY, NO CONNECTOR LIBRARY.** The whole flow is one `personal_sign`; a connector
 * stack would be a dependency larger than the feature.
 *
 * ⚠️ **THE MESSAGE COMES FROM THE SERVER.** Composing it here would let the two drift and the
 * signature would then verify against nothing.
 */
export type LinkOutcome =
  | { ok: true; address: string }
  | { ok: false; reason: string; cancelled: boolean };

export async function linkWalletFlow(): Promise<LinkOutcome> {
  const eth = (window as unknown as { ethereum?: {
    request(a: { method: string; params?: unknown[] }): Promise<unknown>;
  } }).ethereum;
  if (!eth) {
    return { ok: false, cancelled: false,
             reason: "No wallet found in this browser. Install MetaMask or open the game in a wallet browser." };
  }
  try {
    const accounts = await eth.request({ method: "eth_requestAccounts" }) as string[];
    const from = accounts?.[0];
    if (!from) return { ok: false, cancelled: true, reason: "No account was shared." };

    const { nonce, message } = await Api.gateNonce();
    const signature = await eth.request({
      method: "personal_sign", params: [message, from],
    }) as string;

    const r = await Api.linkWallet(nonce, signature);
    return { ok: true, address: r.primaryWalletAddress };
  } catch (e) {
    // A user rejecting the signature is not an error worth shouting about.
    const msg = e instanceof ApiError ? (e.body || e.message) : String(e);
    const cancelled = /reject|denied|4001/i.test(msg);
    return { ok: false, cancelled, reason: cancelled ? "Signature cancelled." : msg };
  }
}
